import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AppState } from '@app/store/state/app.state';
import { DailyWhereaboutsResponse } from '@shared/models/api.models';
import { MemberMovements, UsersWhereaboutsDay } from '@features/whereabouts/models/movements.models';
import { requestDailyWhereabouts, requestUsersWeeklyMovements } from '@features/whereabouts/services/Movements';
import { Moment } from 'moment';

function replaceDayInMember(member: MemberMovements, replacement: UsersWhereaboutsDay): MemberMovements {
  if (member.id !== replacement.userId) return member;
  const whereabouts = member.whereabouts ?? [];
  const exists = whereabouts.some(w => w.date === replacement.date);
  return {
    ...member,
    whereabouts: exists
      ? whereabouts.map(w => w.date === replacement.date ? replacement : w)
      : [...whereabouts, replacement],
  };
}

export interface DailyWhereaboutsState {
  data: DailyWhereaboutsResponse | null;
  loading: boolean;
  currentUserData: MemberMovements | null;
}

export const initialDailyWhereaboutsState: DailyWhereaboutsState = {
  data: null,
  loading: false,
  currentUserData: null,
};

export const loadDailyWhereabouts: any = createAsyncThunk(
  'dailyWhereabouts/load',
  async (params: { date: string; teamIds: string[]; workGroupIds: number[] }) => {
    if (params.teamIds.length === 0 && params.workGroupIds.length === 0) {
      return null;
    }
    return await requestDailyWhereabouts(params.date, params.teamIds, params.workGroupIds);
  },
);

export const loadCurrentUserDailyData: any = createAsyncThunk(
  'dailyWhereabouts/loadCurrentUser',
  async (params: { calendarDate: Moment; userId: string }) => {
    return await requestUsersWeeklyMovements(params.calendarDate, params.userId);
  },
);

const slice = createSlice({
  name: 'dailyWhereabouts',
  initialState: initialDailyWhereaboutsState,
  reducers: {
    clearDailyData: (state) => {
      state.data = null;
      state.loading = false;
    },
    setDailyLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    updateDailyMemberWhereabouts: (state, action: PayloadAction<UsersWhereaboutsDay>) => {
      const replacement = action.payload;
      if (state.currentUserData) {
        state.currentUserData = replaceDayInMember(state.currentUserData, replacement);
      }
      if (!state.data) return;
      state.data.teamWhereabouts = state.data.teamWhereabouts.map(team => ({
        ...team,
        teamWhereabouts: (team.teamWhereabouts ?? []).map(m => replaceDayInMember(m, replacement)),
      }));
      state.data.workgroupsWhereabouts = state.data.workgroupsWhereabouts.map(wg => ({
        ...wg,
        whereabouts: (wg.whereabouts ?? []).map(m => replaceDayInMember(m, replacement)),
      }));
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadDailyWhereabouts.pending, (state) => {
        state.loading = true;
      })
      .addCase(loadDailyWhereabouts.rejected, (state) => {
        state.data = null;
        state.loading = false;
      })
      .addCase(loadDailyWhereabouts.fulfilled, (state, action: PayloadAction<DailyWhereaboutsResponse | null>) => {
        state.data = action.payload;
        state.loading = false;
      })
      .addCase(loadCurrentUserDailyData.rejected, (state) => {
        state.currentUserData = null;
      })
      .addCase(loadCurrentUserDailyData.fulfilled, (state, action: PayloadAction<MemberMovements>) => {
        state.currentUserData = action.payload;
      });
  },
});

export default slice.reducer;
export const { clearDailyData, setDailyLoading, updateDailyMemberWhereabouts } = slice.actions;

export const selectDailyData = (state: AppState) => state.dailyWhereabouts.data;
export const selectDailyLoading = (state: AppState) => state.dailyWhereabouts.loading;
export const selectDailyCurrentUserData = (state: AppState) => state.dailyWhereabouts.currentUserData;
